import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  RelatorioOfertasResumo,
  RelatorioTermos,
  RelOfertasFiltro,
  RelOfertasPagina,
  RelOfertasUnidadesDisponiveis,
} from '../../interfaces/relatorios.dto';
import { mensagemDoBlob, nomeDoArquivo, salvarBlob } from '../../utils/baixar-arquivo.util';

@Injectable({ providedIn: 'root' })
export class RelatoriosService {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/relatorios`;

  constructor(private http: HttpClient) {}

  /** Erro sobe com a mensagem real da API. */
  async termos(cpf: string): Promise<RelatorioTermos> {
    try {
      const resposta = await firstValueFrom(this.http.get<any>(`${this.apiURL}/termos`, {
        params: new HttpParams().set('cpf', cpf),
      }));
      return resposta?.data?.[0] ?? { assinadoEm: null, termos: [] };
    } catch (e: any) {
      throw new Error(e?.error?.notifications?.[0]?.message ?? 'Registro de termos indisponível agora.');
    }
  }

  async baixarTermo(id: string, cpf: string): Promise<void> {
    try {
      const resposta = await firstValueFrom(this.http.get(`${this.apiURL}/termos/${encodeURIComponent(id)}/arquivo`, {
        params: new HttpParams().set('cpf', cpf),
        responseType: 'blob',
        observe: 'response',
      }));
      salvarBlob(resposta.body as Blob, nomeDoArquivo(resposta.headers.get('Content-Disposition'), 'termo.pdf'));
    } catch (e: any) {
      throw new Error(await mensagemDoBlob(e, 'Não foi possível baixar o termo agora.'));
    }
  }

  private paramsOfertas(f: RelOfertasFiltro): HttpParams {
    return new HttpParams().set('de', f.de).set('ate', f.ate).set('unidade', f.unidade);
  }

  async ofertasUnidades(): Promise<RelOfertasUnidadesDisponiveis> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/ofertas/unidades`));
      return r?.data?.[0] ?? { administrador: false, unidades: [] };
    } catch (e: any) {
      throw new Error(e?.error?.notifications?.[0]?.message ?? 'Unidades indisponíveis agora.');
    }
  }

  async ofertasResumo(f: RelOfertasFiltro): Promise<RelatorioOfertasResumo> {
    try {
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/ofertas/resumo`, { params: this.paramsOfertas(f) }));
      return r?.data?.[0];
    } catch (e: any) {
      throw new Error(e?.error?.notifications?.[0]?.message ?? 'Relatório indisponível agora.');
    }
  }

  async ofertasLista(f: RelOfertasFiltro, bloco: string, chave: string, pagina: number): Promise<RelOfertasPagina> {
    try {
      const params = this.paramsOfertas(f).set('bloco', bloco).set('chave', chave).set('pagina', String(pagina));
      const r = await firstValueFrom(this.http.get<any>(`${this.apiURL}/ofertas/lista`, { params }));
      return { itens: r?.data ?? [], total: r?.total ?? 0 };
    } catch (e: any) {
      throw new Error(e?.error?.notifications?.[0]?.message ?? 'Lista indisponível agora.');
    }
  }

  async exportarOfertas(f: RelOfertasFiltro): Promise<void> {
    try {
      const resposta = await firstValueFrom(this.http.get(`${this.apiURL}/ofertas/exportar`,
        { params: this.paramsOfertas(f), responseType: 'blob', observe: 'response' }));
      salvarBlob(resposta.body as Blob, nomeDoArquivo(resposta.headers.get('Content-Disposition'), 'relatorio-ofertas.csv'));
    } catch (e: any) {
      throw new Error(await mensagemDoBlob(e, 'Não foi possível exportar agora.'));
    }
  }
}
